<script setup lang="ts">
import Notification from '@/components/notifications/Notification.vue';
import { formBackendErrorHandler } from '@/errorHandling';
import { useAppUpdate } from '@/utils/useAppUpdate';
import { useTranslation } from 'i18next-vue';
import { upperFirst } from 'lodash';
import type { VeeFormContext } from '@/components/forms/useVeeForm';
import { computed } from 'vue';
import { UpdateType } from './UpdateWizard.vue';

const { t } = useTranslation();

export type WizardStepSecurityProps = {
  type: UpdateType;
  form: VeeFormContext;
};

const { type, form } = defineProps<WizardStepSecurityProps>();

const { state, notification } = useAppUpdate(t, formBackendErrorHandler(form, true));

const text = computed(() => {
  return {
    idleHeader: t('_updateType_Status', { updateType: upperFirst(type) }),
    idleMessage: t('No_updateType_InProgress', {
      updateType: type,
    }),
    successHeader: t('_updateType_Successful', {
      updateType: upperFirst(type),
    }),
    successMessage: undefined,
    failureHeader: t('_updateType_Failed', {
      updateType: upperFirst(type),
    }),
    failureMessage: t('The_updateType_CouldNotBeApplied', {
      updateType: type,
    }),
  };
});

const header = computed(() => {
  switch (state.value) {
    case 'success':
      return text.value.successHeader;
    case 'failed':
      return text.value.failureMessage;
    case 'idle':
      return text.value.idleHeader;
    default:
      throw new Error(`Unknown update state ${state.value}`);
  }
});

const message = computed(() => {
  switch (state.value) {
    case 'success':
      return text.value.successMessage;
    case 'failed':
      return text.value.failureMessage;
    case 'idle':
      return text.value.idleMessage;
    default:
      throw new Error(`Unknown update state ${state.value}`);
  }
});
</script>

<template>
  <h2 class="mb-8 mt-5 on-off-white">
    {{ header }}
  </h2>

  <Notification class="mt-8" :value="notification" />

  <p v-if="message" class="text-pre-line mt-5">
    {{ message }}
  </p>
</template>
